/**
 * Lightweight UI string translations for the patient-facing screens.
 * Only patient-facing copy is localized. Clinician toolbar stays in English.
 *
 * Keep all 8 supported language codes in sync with /api/languages.
 */
const STRINGS = {
  en: {
    forPatient: "For {name}",
    yourReportTitle: "Your {modality} report, explained",
    listen: "Listen to your report",
    clinicianNoteLabel: "A note from your clinician",
    addNoteButton: "Add a personal note for the patient",
    addNotePlaceholder: "e.g. Glad to share good news! Please book a follow-up in 3 months.",
    saveNote: "Save note",
    cancel: "Cancel",
    edit: "Edit",
    sharedGreeting: "Hello {name}, here's your report",
    disclaimer: "This summary was written by an AI assistant based on your radiology report. Please discuss it with your doctor before making any medical decisions.",
    sharedDisclaimer: "This summary was prepared with the help of an AI assistant based on your imaging report. Please review it with your doctor.",
    voiceLabel: "Voice",
    print: "Print",
    translatedByAi: "Translated by AI from {source}.",
  },
  es: {
    forPatient: "Para {name}",
    yourReportTitle: "Tu informe de {modality}, explicado",
    listen: "Escucha tu informe",
    clinicianNoteLabel: "Una nota de tu médico",
    addNoteButton: "Añadir una nota personal para el paciente",
    addNotePlaceholder: "ej. ¡Buenas noticias! Por favor agenda un seguimiento en 3 meses.",
    saveNote: "Guardar nota",
    cancel: "Cancelar",
    edit: "Editar",
    sharedGreeting: "Hola {name}, aquí está tu informe",
    disclaimer: "Este resumen fue escrito por un asistente de IA basado en tu informe de radiología. Por favor coméntalo con tu médico antes de tomar cualquier decisión médica.",
    sharedDisclaimer: "Este resumen fue preparado con la ayuda de un asistente de IA a partir de tu informe de imágenes. Por favor revísalo con tu médico.",
    voiceLabel: "Voz",
    print: "Imprimir",
    translatedByAi: "Traducido por IA desde {source}.",
  },
  fr: {
    forPatient: "Pour {name}",
    yourReportTitle: "Votre compte rendu de {modality}, expliqué",
    listen: "Écoutez votre compte rendu",
    clinicianNoteLabel: "Un mot de votre médecin",
    addNoteButton: "Ajouter une note personnelle au patient",
    addNotePlaceholder: "ex. Bonne nouvelle ! Merci de prendre un rendez-vous de suivi dans 3 mois.",
    saveNote: "Enregistrer la note",
    cancel: "Annuler",
    edit: "Modifier",
    sharedGreeting: "Bonjour {name}, voici votre compte rendu",
    disclaimer: "Ce résumé a été rédigé par un assistant IA à partir de votre compte rendu de radiologie. Veuillez en discuter avec votre médecin avant toute décision médicale.",
    sharedDisclaimer: "Ce résumé a été préparé avec l'aide d'un assistant IA à partir de votre compte rendu d'imagerie. Merci de l'examiner avec votre médecin.",
    voiceLabel: "Voix",
    print: "Imprimer",
    translatedByAi: "Traduit par IA depuis {source}.",
  },
  de: {
    forPatient: "Für {name}",
    yourReportTitle: "Ihr {modality}-Befund, verständlich erklärt",
    listen: "Hören Sie sich Ihren Befund an",
    clinicianNoteLabel: "Eine Nachricht von Ihrer Ärztin / Ihrem Arzt",
    addNoteButton: "Persönliche Notiz für den Patienten hinzufügen",
    addNotePlaceholder: "z. B. Gute Nachrichten! Bitte vereinbaren Sie in 3 Monaten einen Folgetermin.",
    saveNote: "Notiz speichern",
    cancel: "Abbrechen",
    edit: "Bearbeiten",
    sharedGreeting: "Hallo {name}, hier ist Ihr Befund",
    disclaimer: "Diese Zusammenfassung wurde von einem KI-Assistenten auf Basis Ihres Radiologie-Befunds verfasst. Bitte besprechen Sie sie mit Ihrer Ärztin / Ihrem Arzt, bevor Sie medizinische Entscheidungen treffen.",
    sharedDisclaimer: "Diese Zusammenfassung wurde mithilfe eines KI-Assistenten aus Ihrem Bildgebungs-Befund erstellt. Bitte besprechen Sie sie mit Ihrer Ärztin / Ihrem Arzt.",
    voiceLabel: "Stimme",
    print: "Drucken",
    translatedByAi: "Von KI aus dem {source} übersetzt.",
  },
  pt: {
    forPatient: "Para {name}",
    yourReportTitle: "O seu relatório de {modality}, explicado",
    listen: "Ouça o seu relatório",
    clinicianNoteLabel: "Uma nota do seu médico",
    addNoteButton: "Adicionar uma nota pessoal para o paciente",
    addNotePlaceholder: "ex. Boas notícias! Por favor marque uma consulta de seguimento em 3 meses.",
    saveNote: "Guardar nota",
    cancel: "Cancelar",
    edit: "Editar",
    sharedGreeting: "Olá {name}, aqui está o seu relatório",
    disclaimer: "Este resumo foi escrito por um assistente de IA com base no seu relatório de radiologia. Por favor converse com o seu médico antes de tomar qualquer decisão médica.",
    sharedDisclaimer: "Este resumo foi preparado com a ajuda de um assistente de IA a partir do seu exame de imagem. Por favor reveja com o seu médico.",
    voiceLabel: "Voz",
    print: "Imprimir",
    translatedByAi: "Traduzido por IA do {source}.",
  },
  hi: {
    forPatient: "{name} के लिए",
    yourReportTitle: "आपकी {modality} रिपोर्ट, आसान भाषा में",
    listen: "अपनी रिपोर्ट सुनें",
    clinicianNoteLabel: "आपके डॉक्टर का संदेश",
    addNoteButton: "मरीज़ के लिए व्यक्तिगत संदेश जोड़ें",
    addNotePlaceholder: "उदा. अच्छी खबर है! कृपया 3 महीने में फॉलो-अप का समय तय करें।",
    saveNote: "संदेश सहेजें",
    cancel: "रद्द करें",
    edit: "बदलें",
    sharedGreeting: "नमस्ते {name}, यह रही आपकी रिपोर्ट",
    disclaimer: "यह सारांश आपकी रेडियोलॉजी रिपोर्ट के आधार पर एक AI सहायक द्वारा लिखा गया है। कोई भी चिकित्सकीय निर्णय लेने से पहले कृपया अपने डॉक्टर से चर्चा करें।",
    sharedDisclaimer: "यह सारांश आपकी इमेजिंग रिपोर्ट से एक AI सहायक की मदद से तैयार किया गया है। कृपया अपने डॉक्टर के साथ इसकी समीक्षा करें।",
    voiceLabel: "आवाज़",
    print: "प्रिंट करें",
    translatedByAi: "AI द्वारा {source} से अनुवादित।",
  },
  ar: {
    forPatient: "إلى {name}",
    yourReportTitle: "تقرير {modality} الخاص بك، بشرح مبسط",
    listen: "استمع إلى تقريرك",
    clinicianNoteLabel: "ملاحظة من طبيبك",
    addNoteButton: "أضف ملاحظة شخصية للمريض",
    addNotePlaceholder: "مثلاً: أخبار سارة! يرجى حجز موعد متابعة بعد 3 أشهر.",
    saveNote: "حفظ الملاحظة",
    cancel: "إلغاء",
    edit: "تعديل",
    sharedGreeting: "مرحباً {name}، هذا هو تقريرك",
    disclaimer: "تمت كتابة هذا الملخص بواسطة مساعد ذكاء اصطناعي استناداً إلى تقرير الأشعة الخاص بك. يرجى مناقشته مع طبيبك قبل اتخاذ أي قرارات طبية.",
    sharedDisclaimer: "تم إعداد هذا الملخص بمساعدة مساعد ذكاء اصطناعي استناداً إلى تقرير التصوير الخاص بك. يرجى مراجعته مع طبيبك.",
    voiceLabel: "الصوت",
    print: "طباعة",
    translatedByAi: "تُرجم بواسطة الذكاء الاصطناعي من {source}.",
  },
  zh: {
    forPatient: "致 {name}",
    yourReportTitle: "您的 {modality} 报告解读",
    listen: "聆听您的报告",
    clinicianNoteLabel: "来自您的医生的留言",
    addNoteButton: "为患者添加一条个人留言",
    addNotePlaceholder: "例如:好消息!请在 3 个月后预约复诊。",
    saveNote: "保存留言",
    cancel: "取消",
    edit: "编辑",
    sharedGreeting: "{name},您好,这是您的报告",
    disclaimer: "本摘要由 AI 助手根据您的放射学报告撰写。在做出任何医疗决定之前,请与您的医生讨论。",
    sharedDisclaimer: "本摘要在 AI 助手的协助下根据您的影像报告生成。请与您的医生一同审阅。",
    voiceLabel: "声音",
    print: "打印",
    translatedByAi: "由 AI 从{source}翻译。",
  },
};

/** Native-language label for an ISO code, used by translatedByAi footnote. */
export const NATIVE_NAMES = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  pt: "Português",
  hi: "हिन्दी",
  ar: "العربية",
  zh: "中文",
};

export function t(lang, key, vars = {}) {
  const dict = STRINGS[lang] || STRINGS.en;
  let value = dict[key] ?? STRINGS.en[key] ?? key;
  for (const [k, v] of Object.entries(vars)) {
    value = value.replace(new RegExp(`\\{${k}\\}`, "g"), v);
  }
  return value;
}

export default STRINGS;
